import { parsePhoneNumberFromString } from "libphonenumber-js/min";
import { GetUserInfo } from "./getUserInfo";

// Returns the active WhatsApp integration row for the logged-in customer
// (the row in auth.integrations with attribute_5 === "Whatsapp" and
// active === "Yes"), or null when not configured.
//
// Mirrors the parsing in isIntegrationActive (getUserInfo.js) but yields
// the full row so callers can read `id` as integrationId.
export const getWhatsappIntegration = () => {
  const userInfo = GetUserInfo();
  const raw = userInfo?.[0]?.integrations;
  if (!raw) return null;
  let parsed;
  try {
    parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
  } catch {
    return null;
  }
  if (!Array.isArray(parsed)) return null;
  return (
    parsed.find(
      (row) => row.attribute_5 === "Whatsapp" && row.active === "Yes",
    ) || null
  );
};

// Loose check used to decide whether the WhatsApp icon should appear at
// all on an applicant. We don't want a strict E.164 check here — the
// applicant's stored number may be missing the country code, and the
// user can fix it inside the drawer. So we only require *some* digits.
export const hasAnyPhoneNumber = (raw) => {
  if (!raw) return false;
  return String(raw).replace(/[^\d]/g, "").length >= 7;
};

// Parse a raw phone string into a structured result the drawer uses to
// drive validation messaging, formatted display, and the Send-button
// enabled/disabled state. We use libphonenumber-js (min build) so the
// rules adapt per country instead of one-size-fits-all length checks.
//
// Behaviour:
//   - Input already E.164 (`+...`): parsed without needing a default region.
//   - Input is local digits (`8466880194`): only parses to a valid number
//     when `defaultCountry` is provided AND the digits form a valid number
//     in that country. Otherwise it's invalid — we deliberately don't
//     guess. Sending to the wrong country is worse than refusing to send.
//
// Return shape is always defined (never throws), so callers can branch
// on `valid` without try/catch.
export const parsePhone = (raw, defaultCountry) => {
  const empty = { valid: false, e164: null, formatted: "", country: null, error: "empty" };
  if (!raw) return empty;
  const trimmed = String(raw).trim();
  if (!trimmed) return empty;

  let parsed;
  try {
    parsed = parsePhoneNumberFromString(trimmed, defaultCountry || undefined);
  } catch {
    parsed = null;
  }

  if (!parsed) {
    // Couldn't even tokenize a phone number out of the input
    return {
      valid: false,
      e164: null,
      formatted: trimmed,
      country: null,
      error: trimmed.startsWith("+")
        ? "Number is too short or contains invalid characters"
        : "Add country code (e.g. +91) or pick the candidate's country",
    };
  }

  if (!parsed.isValid()) {
    return {
      valid: false,
      e164: parsed.number || null,
      formatted: parsed.formatInternational?.() || trimmed,
      country: parsed.country || null,
      error: parsed.country
        ? `Not a valid ${parsed.country} number`
        : "Not a valid phone number",
    };
  }

  return {
    valid: true,
    e164: parsed.number,
    formatted: parsed.formatInternational(),
    country: parsed.country || null,
    error: null,
  };
};

// Back-compat wrapper. Existing call sites just want the E.164 string
// (or null) — keep them working while the drawer migrates to the richer
// `parsePhone` result above.
export const normalizeToE164 = (raw, defaultCountry) => {
  const result = parsePhone(raw, defaultCountry);
  return result.valid ? result.e164 : null;
};
